import axios from 'axios';

export interface User {
  id: string;
  email: string;
  name: string;
}

export interface EventProfile {
  eventId: string;
  userId: string;
  role: string;
  permissions: string[];
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

export class AuthService {
  private baseUrl: string;
  private tokens: AuthTokens | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.loadTokensFromStorage();
  }

  private loadTokensFromStorage(): void {
    const storedTokens = localStorage.getItem('auth_tokens');
    if (storedTokens) {
      try {
        this.tokens = JSON.parse(storedTokens);
        console.log('[AuthService] Loaded tokens from storage');
      } catch (e) {
        console.error('[AuthService] Failed to parse stored tokens:', e);
        localStorage.removeItem('auth_tokens');
      }
    } else {
      console.log('[AuthService] No tokens found in storage');
    }
  }

  private saveTokensToStorage(tokens: AuthTokens): void {
    this.tokens = tokens;
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    console.log('[AuthService] Saved tokens to storage');
  }

  private clearTokensFromStorage(): void {
    this.tokens = null;
    localStorage.removeItem('auth_tokens');
  }

  public getAccessToken(): string | null {
    return this.tokens?.accessToken || null;
  }

  public async validateToken(token: string): Promise<boolean> {
    try {
      const response = await axios.post(`${this.baseUrl}/api/v1/auth/validate`, {
        token,
      });
      return response.data.valid;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }

  public async getEventProfile(eventId: string, userId?: string): Promise<EventProfile | null> {
    if (!this.tokens?.accessToken) {
      throw new Error('No authentication token available');
    }

    try {
      // Use provided userId or fetch current user
      let userIdToUse = userId;
      if (!userIdToUse) {
        const currentUser = await this.getCurrentUser();
        if (!currentUser) {
          throw new Error('No current user found');
        }
        userIdToUse = currentUser.id;
      }

      const response = await axios.get(`${this.baseUrl}/api/v1/events/${eventId}/users/${userIdToUse}/profile`, {
        headers: {
          Authorization: `Bearer ${this.tokens.accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch event profile:', error);
      return null;
    }
  }

  public async getCurrentUser(): Promise<User | null> {
    if (!this.tokens?.accessToken) {
      return null;
    }

    try {
      const response = await axios.get(`${this.baseUrl}/api/v1/auth/me`, {
        headers: {
          Authorization: `Bearer ${this.tokens.accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      return null;
    }
  }

  public async refreshToken(): Promise<boolean> {
    if (!this.tokens?.refreshToken) {
      return false;
    }

    try {
      const response = await axios.post(`${this.baseUrl}/api/v1/auth/refresh`, {
        refreshToken: this.tokens.refreshToken,
      });
      
      this.saveTokensToStorage(response.data);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearTokensFromStorage();
      return false;
    }
  }

  public setTokens(tokens: AuthTokens): void {
    console.log('[AuthService] Setting tokens:', { hasAccessToken: !!tokens.accessToken, hasRefreshToken: !!tokens.refreshToken });
    this.saveTokensToStorage(tokens);
  }

  public logout(): void {
    this.clearTokensFromStorage();
  }

  public isAuthenticated(): boolean {
    const isAuth = !!this.tokens?.accessToken;
    console.log('[AuthService] isAuthenticated:', isAuth, 'hasTokens:', !!this.tokens);
    return isAuth;
  }
}